import { defineStore } from 'pinia'
import StorageService from '~/services/StorageService'
import { toInitials } from '~/cynqueModules/utils/toInitials'
import { useNuxtRouter } from '~/cynqueModules/nuxtRuntime'

export interface IStoredUser {
  firstName?: string
  lastName?: string
  [key: string]: unknown
}

/**
 * Converted from store/user.js.
 *
 * The state field behind the `hasALogin` getter is renamed `hasALoginFlag`: in
 * Vuex the getter and the field shared a name, which in Pinia makes the field a
 * readonly computed and silently swallows every write to it. The getter name is
 * the public one, so the field is what moves. Safe — this module is not
 * persisted, so no stored blob names it.
 *
 * LOGOUT's `this.$router` is gone. Nuxt 2 hung the router off the root store and
 * plugins/00.store.ts re-attached it by hand for that one mutation; in Pinia
 * `this` is the store, so the router comes from the runtime singleton instead.
 */
export const useUserStore = defineStore('user', {
  state: () => ({
    user: null as IStoredUser | null,
    hasALoginFlag: false,
  }),

  getters: {
    hasALogin: (state) => !!state.hasALoginFlag,
    isLoggedIn: (state) => !!state.user,
    getUser: (state) => {
      if (!state.user) {
        // eslint-disable-next-line no-console
        console.debug('[stores/user.ts] getUser | No user; returning null')
        return null
      }
      return state.user
    },
    getName: (state): string | null => {
      if (!state.user) {
        // eslint-disable-next-line no-console
        console.debug(
          '[stores/user.ts] getName | No user; returning name = null'
        )
        return null
      }
      return state.user.firstName + ' ' + state.user.lastName
    },
    getInitials(state): string | null {
      // A method getter, not an arrow: Pinia option getters receive only
      // `state`, so reading the sibling getter (Vuex's second argument) needs
      // `this`.
      if (!state.user) {
        // eslint-disable-next-line no-console
        console.debug(
          '[stores/user.ts] getInitials | No user; returning initials = null'
        )
        return null
      }
      return toInitials(this.getName as string)
    },
  },

  actions: {
    set(userData: IStoredUser): void {
      const fn = '[stores/user.ts] set |'
      this.user = userData
      // eslint-disable-next-line no-console
      console.debug(`${fn} typeof userData = ${typeof userData}`)
      StorageService.setUser(userData)
      this.hasALoginFlag = true
      StorageService.setHasALogin()
      // eslint-disable-next-line no-console
      console.debug(`${fn} Set state.hasALogin to ${this.hasALoginFlag}`)
      StorageService.log(fn)
    },

    logout(): void {
      StorageService.removeUser()
      this.user = null
      useNuxtRouter().push({ name: 'Login' })
    },

    setHasALogin(): void {
      this.hasALoginFlag = true
      // eslint-disable-next-line no-console
      console.debug(
        `[stores/user.ts] setHasALogin | Set hasALogin to ${this.hasALoginFlag}`
      )
    },
  },
})
